// Programmatic lens renderer (Lenses piece 3, post-pivot).
//
// This is the daemon-side render executor. It runs when a scoped client sends
// `lenses_render_request`. There is no LLM in the loop — a lens render is
// deterministic:
//
//   1. Read `<cwd>/_appostle-lenses/<lensSlug>/prose.md` (v1.1 folder-per-lens structure).
//   2. Parse YAML frontmatter; fuse the Mystic prompt from H2 sections in a
//      fixed order (Visual Identity → Subject & Composition → Lens & Camera →
//      Lighting & Atmosphere → Color & Palette → Materials & Texture). Prepend
//      the sanitized client subject; append `Avoid: ` + Forbidden bullets.
//      `{{key}}` placeholders are substituted from validated variables +
//      internal knobs.
//   3. POST to `api.freepik.com/v1/ai/mystic` with `x-freepik-api-key: FREEPIK_API_KEY`.
//   4. Poll every 5s (max 60 attempts) until COMPLETED or FAILED.
//   5. Download the finished image, save under
//      `<cwd>/_appostle-lenses/<lensSlug>/renders/<YYYY-MM-DD>/<imageSlug>.webp`.
//   6. Append a per-lens `renders/log.jsonl` entry; also append to the daemon
//      metering log under `$APPOSTLE_HOME/lenses/renders-<sha8(userId)>.jsonl`.
//   7. Emit progress + a data-URI back to the client.
//
// Errors are mapped to structured codes so the client can render friendly
// copy. No LLM, no agent spawning — this is a plain deterministic pipeline.
//
// The FREEPIK_API_KEY env var is read once (by session.ts) and threaded into
// this function. When absent → `magnific_key_missing` error, no throw.

import { readFile, mkdir, writeFile, appendFile } from "node:fs/promises";
import { createHash, randomBytes } from "node:crypto";
import { join } from "path";
import { parse as parseYaml } from "yaml";
import type pino from "pino";
import { sanitizeSubject } from "./client-scope.js";
import { isValidUploadId, uploadsDir } from "./uploads.js";
import { resolveLensDir } from "./paths.js";
import { runMotionRender } from "./motion.js";

const MYSTIC_ENDPOINT = "https://api.freepik.com/v1/ai/mystic";

function lensDir(cwd: string, slug: string): string {
  return resolveLensDir(cwd, slug);
}

function todayFolder(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export type RenderStage = "posted" | "polling" | "downloading" | "saved";

export interface RunLensRenderArgs {
  cwd: string;
  lensSlug: string;
  /** Empty string for subject-less (composite) lenses. */
  subject: string;
  variables: Record<string, string>;
  /** Composite lenses: input key → uploadId (from `lenses_upload_response`). */
  inputs?: Record<string, string>;
  clientUserId: string;
  appostleHome: string;
  magnificApiKey: string | null;
  logger: pino.Logger;
  onProgress: (stage: RenderStage, detail?: string) => void;
}

export interface RunLensRenderResult {
  ok: boolean;
  imageUrl?: string;
  imageSlug?: string;
  error?: string;
  errorCode?: string;
}

// ---- Prompt fusion ---------------------------------------------------------

const SECTION_ORDER = [
  "Visual Identity",
  "Subject & Composition",
  "Lens & Camera",
  "Lighting & Atmosphere",
  "Color & Palette",
  "Materials & Texture",
] as const;

function extractFrontmatter(source: string): { fm: Record<string, unknown>; body: string } {
  if (!source.startsWith("---")) return { fm: {}, body: source };
  const end = source.indexOf("\n---", 3);
  if (end === -1) return { fm: {}, body: source };
  const yamlBlock = source.slice(source.indexOf("\n") + 1, end);
  const body = source.slice(end + 4);
  try {
    const fm = (parseYaml(yamlBlock) as Record<string, unknown>) ?? {};
    return { fm, body };
  } catch {
    return { fm: {}, body };
  }
}

/**
 * Extracts the paragraph/bullets under an `## <name>` heading, up to the next
 * H2 heading or the end of the doc. Case-insensitive on the heading text.
 */
function extractH2Section(body: string, name: string): string {
  const lines = body.split("\n");
  const marker = `## ${name.toLowerCase()}`;
  let inSection = false;
  const collected: string[] = [];
  for (const line of lines) {
    const lower = line.toLowerCase().trim();
    if (lower.startsWith("## ")) {
      if (inSection) break;
      if (lower === marker) inSection = true;
      continue;
    }
    if (inSection) collected.push(line);
  }
  return collected.join("\n").trim();
}

function extractForbidden(body: string): string[] {
  const section = extractH2Section(body, "Forbidden");
  if (!section) return [];
  return section
    .split("\n")
    .map((l) => l.replace(/^\s*[-*]\s*/, "").trim())
    .filter((l) => l.length > 0);
}

function substitutePlaceholders(input: string, values: Record<string, string>): string {
  return input.replace(/\{\{([a-zA-Z0-9_-]+)\}\}/g, (_, key: string) => {
    const v = values[key];
    return typeof v === "string" ? v : "";
  });
}

function fusePrompt(args: {
  subject: string;
  body: string;
  values: Record<string, string>;
}): string {
  const parts: string[] = [args.subject.trim()];
  for (const name of SECTION_ORDER) {
    const section = extractH2Section(args.body, name);
    if (section) parts.push(substitutePlaceholders(section, args.values));
  }
  const forbidden = extractForbidden(args.body);
  if (forbidden.length > 0) {
    parts.push(`Avoid: ${forbidden.join("; ")}`);
  }
  return parts.join("\n\n");
}

// ---- Magnific mapping ------------------------------------------------------

function resolutionFor(quality: string | undefined): "1k" | "2k" {
  return quality === "final" ? "2k" : "1k";
}

function aspectRatioFor(
  format: string | undefined,
): "square_1_1" | "social_story_9_16" | "widescreen_16_9" | "portrait_3_4" {
  switch (format) {
    case "story":
      return "social_story_9_16";
    case "landscape":
    case "banner":
      return "widescreen_16_9";
    case "portrait":
      return "portrait_3_4";
    default:
      return "square_1_1";
  }
}

function kebab(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

// ---- Renderer --------------------------------------------------------------

export async function runLensRender(args: RunLensRenderArgs): Promise<RunLensRenderResult> {
  const {
    cwd,
    lensSlug,
    subject,
    variables,
    clientUserId,
    appostleHome,
    magnificApiKey,
    logger,
    onProgress,
  } = args;

  const log = logger.child({ component: "lens-renderer", lensSlug });

  // 1) Read lens prose.
  const lensPath = join(lensDir(cwd, lensSlug), "prose.md");
  let source: string;
  try {
    source = await readFile(lensPath, "utf8");
  } catch (err) {
    log.warn({ err, lensPath }, "lens prose not found");
    return { ok: false, error: "lens file not found", errorCode: "lens_not_found" };
  }

  const { fm, body } = extractFrontmatter(source);
  const lensBlock = (fm.lens as Record<string, unknown> | undefined) ?? {};
  const internal = (lensBlock.internal as Record<string, unknown> | undefined) ?? {};

  // 2) Subject rules. A lens that declares no `subject` spec takes no
  //    user-typed text at all — reject a non-empty subject instead of fusing
  //    it (composite lenses are all subject-less by design).
  const declaresSubject = Boolean(lensBlock.subject);
  if (!declaresSubject && subject.trim().length > 0) {
    return {
      ok: false,
      error: "this lens does not accept a subject",
      errorCode: "client_subject_invalid",
    };
  }

  // 2a) Motion dispatch — renders locally via the lens's own scratch/render.mjs.
  //     No Magnific/Freepik involvement, so this branch runs before the key check.
  //     Subject-less motion lenses (like davedanny-psychedelic-loop) skip sanitize;
  //     the `declaresSubject` guard above already rejected any typed input.
  if (lensBlock.type === "motion-over-html") {
    let motionSubjectValue = "";
    if (declaresSubject) {
      const motionSubject = sanitizeSubject(subject);
      if (!motionSubject.ok) {
        return {
          ok: false,
          error: motionSubject.error ?? "invalid subject",
          errorCode: "client_subject_invalid",
        };
      }
      motionSubjectValue = motionSubject.value ?? "";
    }
    return runMotionRender({
      cwd,
      lensSlug,
      subject: motionSubjectValue,
      variables,
      inputs: args.inputs,
      clientUserId,
      logger,
      onProgress,
    });
  }

  if (!magnificApiKey) {
    await appendRenderLog(appostleHome, clientUserId, {
      ts: new Date().toISOString(),
      lensSlug,
      cwd,
      status: "error",
      errorCode: "magnific_key_missing",
    });
    await appendPerLensLog(cwd, lensSlug, {
      ts: new Date().toISOString(),
      clientUserId,
      lens: lensSlug,
      inputs: { variables, subject },
      status: "error",
      error: "magnific_key_missing",
    });
    return {
      ok: false,
      error: "FREEPIK_API_KEY not set on daemon",
      errorCode: "magnific_key_missing",
    };
  }

  // 2b) Composite dispatch — everything below this point is the Mystic
  //     (generate) flow; composite runs its own Nano Banana Pro pipeline.
  if (lensBlock.type === "composite") {
    return runCompositeRender({
      args,
      lensBlock,
      internal,
      body,
      log,
    });
  }

  const sanitized = sanitizeSubject(subject);
  if (!sanitized.ok) {
    return {
      ok: false,
      error: sanitized.error ?? "invalid subject",
      errorCode: "client_subject_invalid",
    };
  }

  // Merge validated client variables with any frontmatter internal knobs
  // (internal wins for keys the client shouldn't influence, but for prompt
  // substitution both sources feed the placeholder map).
  const values: Record<string, string> = { ...variables };
  for (const [k, v] of Object.entries(internal)) {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      values[k] = String(v);
    }
  }
  values.subject = sanitized.value ?? "";

  const prompt = fusePrompt({ subject: sanitized.value ?? "", body, values });

  // 3) Map to Mystic body.
  const quality = (variables.quality ?? "draft") as string;
  const format = (variables.format ?? "square") as string;
  const resolution = resolutionFor(quality);
  const aspect_ratio = aspectRatioFor(format);
  const model = (internal.model as string | undefined) ?? "realism";
  const creative_detailing =
    typeof internal.creative_detailing === "number" ? (internal.creative_detailing as number) : 33;

  const mysticBody = {
    prompt,
    resolution,
    aspect_ratio,
    model,
    creative_detailing,
    fixed_generation: false,
  };

  onProgress("posted");
  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "posted",
    subject: sanitized.value,
  });

  // 4) POST to Magnific.
  let taskId: string;
  try {
    const res = await fetch(MYSTIC_ENDPOINT, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-freepik-api-key": magnificApiKey,
      },
      body: JSON.stringify(mysticBody),
    });
    if (res.status === 401) {
      return errorResult("magnific_auth", "Magnific API key rejected");
    }
    if (res.status === 402) {
      return errorResult("magnific_credits", "Magnific credit balance exhausted");
    }
    if (res.status === 429) {
      // Retry once after 30s.
      await sleep(30_000);
      const retry = await fetch(MYSTIC_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", "x-freepik-api-key": magnificApiKey },
        body: JSON.stringify(mysticBody),
      });
      if (!retry.ok)
        return errorResult("magnific_rate_limited", `Mystic POST 429 (retry: ${retry.status})`);
      const retryJson = (await retry.json()) as { data?: { task_id?: string; id?: string } };
      taskId = retryJson.data?.task_id ?? retryJson.data?.id ?? "";
    } else if (!res.ok) {
      const detail = await res.text().catch(() => "");
      return errorResult("magnific_task_failed", `Mystic POST ${res.status}: ${detail}`);
    } else {
      const json = (await res.json()) as { data?: { task_id?: string; id?: string } };
      taskId = json.data?.task_id ?? json.data?.id ?? "";
    }
    if (!taskId) return errorResult("magnific_task_failed", "no task_id in POST response");
  } catch (err) {
    log.error({ err }, "Mystic POST failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "network error");
  }

  // 5) Poll.
  onProgress("polling", `task ${taskId}`);
  const pollUrl = `${MYSTIC_ENDPOINT}/${taskId}`;
  let generatedUrl: string | null = null;
  for (let attempt = 0; attempt < 60; attempt++) {
    await sleep(5000);
    try {
      const res = await fetch(pollUrl, {
        headers: { "x-freepik-api-key": magnificApiKey },
      });
      if (!res.ok) {
        // transient — keep polling
        continue;
      }
      const json = (await res.json()) as {
        data?: { status?: string; generated?: string[] };
      };
      const status = json.data?.status;
      if (status === "COMPLETED") {
        generatedUrl = json.data?.generated?.[0] ?? null;
        break;
      }
      if (status === "FAILED") {
        return errorResult("magnific_task_failed", "Mystic task reported FAILED");
      }
    } catch {
      // network flake, try again
    }
  }
  if (!generatedUrl) {
    return errorResult("magnific_timeout", "Mystic poll exhausted without COMPLETED");
  }

  // 6) Download + save.
  onProgress("downloading");
  const imageSlug = `${lensSlug}-${kebab(sanitized.value ?? "subject")}-${randomBytes(4).toString(
    "hex",
  )}`;
  const outDir = join(lensDir(cwd, lensSlug), "renders", todayFolder());
  const outPath = join(outDir, `${imageSlug}.webp`);
  let bytes: Buffer;
  try {
    const dl = await fetch(generatedUrl);
    if (!dl.ok) return errorResult("magnific_task_failed", `download ${dl.status}`);
    const arr = new Uint8Array(await dl.arrayBuffer());
    bytes = Buffer.from(arr);
    await mkdir(outDir, { recursive: true });
    await writeFile(outPath, bytes);
  } catch (err) {
    log.error({ err }, "image download failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "download failed");
  }

  onProgress("saved", outPath);
  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "saved",
    imageSlug,
    outPath,
  });
  await appendPerLensLog(cwd, lensSlug, {
    ts: new Date().toISOString(),
    clientUserId,
    lens: lensSlug,
    inputs: { variables, subject: sanitized.value },
    status: "saved",
    file: outPath,
  });

  // Return a data-URI so the client renders without an HTTP round-trip.
  // (Pragmatic fallback per plan — issuing a download-token URL would need
  // route wiring beyond the scope of this piece.)
  const dataUri = `data:image/webp;base64,${bytes.toString("base64")}`;
  return { ok: true, imageUrl: dataUri, imageSlug };
}

// ---- Composite branch (Nano Banana Pro) ------------------------------------
//
// `type: composite` lenses place a user-uploaded content asset (screenshot,
// label artwork, …) onto an object locked by the prose. Pipeline (mirrors the
// proven magnific-nano-composite-generate skill / scratch/api.md recipe):
//
//   1. Fuse ONE text prompt: `generate an image:` + a composition preamble
//      built from the selected options' long-form `prompt` strings + the
//      entire prose body. Hard cap `internal.max_prompt_chars` (default 3000).
//      Trim strategy when over: the preamble (user's choices) is untouchable;
//      the BODY is cut from the end at the last newline that fits, and the
//      trimmed char count is logged + recorded in log.jsonl so the agency can
//      slim the prose deliberately rather than being silently truncated.
//   2. reference_images (order matters, ≤3 total): style anchors from
//      `internal.style_reference.images` first (≤2 — URLs pass through,
//      lens-local paths like scratch/anchors/1.png are read from disk), then
//      the user's uploaded content image(s) LAST. Nano Banana Pro only takes
//      public URLs, so local files ride the same tmpfiles.org bridge the
//      manual skill uses (~1h expiry — orders of magnitude longer than a
//      generation needs).
//   3. POST /v1/ai/text-to-image/nano-banana-pro, poll 5s × 60, download,
//      save PNG under renders/<YYYY-MM-DD>/, append log.jsonl — identical
//      persistence contract to the generate branch.

const NANO_BANANA_ENDPOINT = "https://api.freepik.com/v1/ai/text-to-image/nano-banana-pro";
const TMPFILES_UPLOAD_URL = "https://tmpfiles.org/api/v1/upload";
const MAX_STYLE_ANCHORS = 2;
const DEFAULT_MAX_PROMPT_CHARS = 3000;

const NANO_ASPECT_RATIOS = new Set([
  "1:1",
  "2:3",
  "3:2",
  "4:3",
  "3:4",
  "5:4",
  "4:5",
  "16:9",
  "9:16",
  "21:9",
]);
const NANO_RESOLUTIONS = new Set(["1K", "2K", "4K"]);

interface CompositeVariableEntry {
  key?: unknown;
  label?: unknown;
  default?: unknown;
  options?: unknown;
}

interface CompositeOptionObject {
  value?: unknown;
  label?: unknown;
  prompt?: unknown;
}

export interface FuseCompositeResult {
  prompt: string;
  /** Chars cut from the END of the prose body to satisfy the cap (0 = intact). */
  trimmedChars: number;
}

/**
 * Fuses the composite prompt. Exported for tests. `aspect_ratio` and
 * `resolution` are API parameters, not prompt content — they're skipped here.
 */
export function fuseCompositePrompt(fuseArgs: {
  lensBlock: Record<string, unknown>;
  body: string;
  variables: Record<string, string>;
  maxChars?: number;
}): FuseCompositeResult {
  const { lensBlock, body, variables } = fuseArgs;
  const maxChars = fuseArgs.maxChars ?? DEFAULT_MAX_PROMPT_CHARS;

  const lines: string[] = [];
  const declared = Array.isArray(lensBlock.variables)
    ? (lensBlock.variables as CompositeVariableEntry[])
    : [];
  for (const entry of declared) {
    if (typeof entry?.key !== "string") continue;
    if (entry.key === "aspect_ratio" || entry.key === "resolution") continue;
    const selected =
      variables[entry.key] ?? (typeof entry.default === "string" ? entry.default : undefined);
    if (selected === undefined) continue;
    const options = Array.isArray(entry.options) ? entry.options : [];
    let wording = selected;
    for (const opt of options) {
      if (typeof opt === "string") {
        if (opt === selected) wording = opt;
        continue;
      }
      const obj = opt as CompositeOptionObject;
      if (obj.value === selected || String(obj.value) === selected) {
        wording =
          (typeof obj.prompt === "string" && obj.prompt) ||
          (typeof obj.label === "string" && obj.label) ||
          selected;
        break;
      }
    }
    const label = typeof entry.label === "string" ? entry.label : entry.key;
    lines.push(`- ${label}: ${wording}`);
  }

  const preamble = `generate an image:\n\nComposition (honor exactly):\n${lines.join("\n")}\n\n`;
  let trimmedBody = body.trim();
  let trimmedChars = 0;
  const budget = maxChars - preamble.length;
  if (trimmedBody.length > budget) {
    const cut = trimmedBody.slice(0, Math.max(0, budget));
    const lastBreak = cut.lastIndexOf("\n");
    const kept = lastBreak > budget / 2 ? cut.slice(0, lastBreak) : cut;
    trimmedChars = trimmedBody.length - kept.length;
    trimmedBody = kept.trimEnd();
  }
  return { prompt: preamble + trimmedBody, trimmedChars };
}

interface NanoReferenceImage {
  image: string;
  text?: string;
  mime_type?: string;
}

function mimeForPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".mp4")) return "video/mp4";
  if (lower.endsWith(".webm")) return "video/webm";
  return "image/png";
}

/** Uploads local bytes to tmpfiles.org and returns the direct-download URL. */
async function uploadToTmpfiles(bytes: Buffer, name: string, mime: string): Promise<string> {
  const form = new FormData();
  form.append("file", new Blob([new Uint8Array(bytes)], { type: mime }), name);
  const res = await fetch(TMPFILES_UPLOAD_URL, { method: "POST", body: form });
  if (!res.ok) throw new Error(`tmpfiles upload failed: ${res.status}`);
  const json = (await res.json()) as { data?: { url?: string } };
  const url = json.data?.url;
  if (!url) throw new Error("tmpfiles upload returned no url");
  return url.replace("tmpfiles.org/", "tmpfiles.org/dl/");
}

async function runCompositeRender(ctx: {
  args: RunLensRenderArgs;
  lensBlock: Record<string, unknown>;
  internal: Record<string, unknown>;
  body: string;
  log: pino.Logger;
}): Promise<RunLensRenderResult> {
  const { args, lensBlock, internal, body, log } = ctx;
  const { cwd, lensSlug, variables, clientUserId, appostleHome, magnificApiKey, onProgress } = args;
  const inputs = args.inputs ?? {};

  // 1) Validate declared inputs vs provided uploadIds.
  const declaredInputs = Array.isArray(lensBlock.inputs)
    ? (lensBlock.inputs as Array<{ key?: unknown; required?: unknown }>)
    : [];
  const contentFiles: Array<{ key: string; path: string }> = [];
  for (const spec of declaredInputs) {
    if (typeof spec?.key !== "string") continue;
    const uploadId = inputs[spec.key];
    if (uploadId === undefined || uploadId === "") {
      if (spec.required === true) {
        return {
          ok: false,
          error: `missing required input: ${spec.key}`,
          errorCode: "client_input_missing",
        };
      }
      continue;
    }
    if (!isValidUploadId(uploadId)) {
      return {
        ok: false,
        error: `invalid uploadId for input ${spec.key}`,
        errorCode: "client_input_invalid",
      };
    }
    const path = join(uploadsDir(cwd, lensSlug), uploadId);
    try {
      await readFile(path); // existence + readability probe
    } catch {
      return {
        ok: false,
        error: `upload not found for input ${spec.key} — upload it again`,
        errorCode: "client_input_missing",
      };
    }
    contentFiles.push({ key: spec.key, path });
  }

  // 2) Fuse prompt.
  const maxChars =
    typeof internal.max_prompt_chars === "number"
      ? (internal.max_prompt_chars as number)
      : DEFAULT_MAX_PROMPT_CHARS;
  const fused = fuseCompositePrompt({ lensBlock, body, variables, maxChars });
  if (fused.trimmedChars > 0) {
    log.warn(
      { trimmedChars: fused.trimmedChars, maxChars },
      "composite prompt exceeded max_prompt_chars — prose body trimmed from the end",
    );
  }

  if (!magnificApiKey) {
    await appendPerLensLog(cwd, lensSlug, {
      ts: new Date().toISOString(),
      clientUserId,
      lens: lensSlug,
      inputs: { variables, uploads: inputs },
      status: "error",
      error: "magnific_key_missing",
    });
    return {
      ok: false,
      error: "FREEPIK_API_KEY not set on daemon",
      errorCode: "magnific_key_missing",
    };
  }

  onProgress("posted", "preparing reference images");

  // 3) Assemble reference images: anchors first (≤2), user content LAST.
  const refs: NanoReferenceImage[] = [];
  const styleRef = (internal.style_reference as { images?: unknown } | undefined) ?? {};
  const anchorList = Array.isArray(styleRef.images)
    ? (styleRef.images as unknown[]).filter((v): v is string => typeof v === "string")
    : [];
  const anchorText =
    "style anchor: register/look reference, NOT screen content; ignore its accent colour, honour the chosen colour variable";
  try {
    for (const anchor of anchorList.slice(0, MAX_STYLE_ANCHORS)) {
      if (/^https?:\/\//i.test(anchor)) {
        refs.push({ image: anchor, text: anchorText, mime_type: mimeForPath(anchor) });
        continue;
      }
      // Lens-local anchor (e.g. scratch/anchors/1.png) — must stay inside the lens dir.
      const resolved = join(lensDir(cwd, lensSlug), anchor);
      if (!resolved.startsWith(lensDir(cwd, lensSlug))) {
        log.warn({ anchor }, "anchor path escapes lens dir — skipped");
        continue;
      }
      const bytes = await readFile(resolved);
      const url = await uploadToTmpfiles(
        bytes,
        anchor.split("/").pop() ?? "anchor.png",
        mimeForPath(anchor),
      );
      refs.push({ image: url, text: anchorText, mime_type: mimeForPath(anchor) });
    }
    for (const [i, file] of contentFiles.entries()) {
      const bytes = await readFile(file.path);
      const url = await uploadToTmpfiles(
        bytes,
        file.path.split("/").pop() ?? "content.png",
        mimeForPath(file.path),
      );
      refs.push({
        image: url,
        text: `screen #${i + 1}: place pixel-perfect on the corresponding device screen; do NOT render a blank screen or placeholder text`,
        mime_type: mimeForPath(file.path),
      });
    }
  } catch (err) {
    log.error({ err }, "reference image preparation failed");
    return errorResult(
      "reference_upload_failed",
      (err as Error)?.message ?? "reference image preparation failed",
    );
  }

  // 4) API params from variables (validated against Nano's accepted sets).
  const aspect_ratio = NANO_ASPECT_RATIOS.has(variables.aspect_ratio ?? "")
    ? (variables.aspect_ratio as string)
    : "1:1";
  const resolution = NANO_RESOLUTIONS.has(variables.resolution ?? "")
    ? (variables.resolution as string)
    : "1K";
  const nanoBody = {
    prompt: fused.prompt,
    reference_images: refs,
    aspect_ratio,
    resolution,
    use_google_search_tool: internal.use_google_search_tool === true,
  };

  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "posted",
    engine: "nano-banana-pro",
  });

  // 5) POST task.
  let taskId: string;
  try {
    const res = await fetch(NANO_BANANA_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json", "x-freepik-api-key": magnificApiKey },
      body: JSON.stringify(nanoBody),
    });
    if (res.status === 401) return errorResult("magnific_auth", "Magnific API key rejected");
    if (res.status === 402)
      return errorResult("magnific_credits", "Magnific credit balance exhausted");
    if (res.status === 429) {
      await sleep(30_000);
      const retry = await fetch(NANO_BANANA_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", "x-freepik-api-key": magnificApiKey },
        body: JSON.stringify(nanoBody),
      });
      if (!retry.ok)
        return errorResult("magnific_rate_limited", `Nano POST 429 (retry: ${retry.status})`);
      const retryJson = (await retry.json()) as { data?: { task_id?: string; id?: string } };
      taskId = retryJson.data?.task_id ?? retryJson.data?.id ?? "";
    } else if (!res.ok) {
      const detail = await res.text().catch(() => "");
      return errorResult("magnific_task_failed", `Nano POST ${res.status}: ${detail}`);
    } else {
      const json = (await res.json()) as { data?: { task_id?: string; id?: string } };
      taskId = json.data?.task_id ?? json.data?.id ?? "";
    }
    if (!taskId) return errorResult("magnific_task_failed", "no task_id in POST response");
  } catch (err) {
    log.error({ err }, "Nano Banana POST failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "network error");
  }

  // 6) Poll.
  onProgress("polling", `task ${taskId}`);
  const pollUrl = `${NANO_BANANA_ENDPOINT}/${taskId}`;
  let generatedUrl: string | null = null;
  for (let attempt = 0; attempt < 60; attempt++) {
    await sleep(5000);
    try {
      const res = await fetch(pollUrl, { headers: { "x-freepik-api-key": magnificApiKey } });
      if (!res.ok) continue;
      const json = (await res.json()) as { data?: { status?: string; generated?: string[] } };
      const status = json.data?.status;
      if (status === "COMPLETED") {
        generatedUrl = json.data?.generated?.[0] ?? null;
        break;
      }
      if (status === "FAILED") {
        return errorResult("magnific_task_failed", "Nano Banana task reported FAILED");
      }
    } catch {
      // network flake, try again
    }
  }
  if (!generatedUrl) {
    return errorResult("magnific_timeout", "Nano Banana poll exhausted without COMPLETED");
  }

  // 7) Download + save (PNG — preserves any transparency the model emits).
  onProgress("downloading");
  const imageSlug = `${lensSlug}-${kebab(variables.device_type ?? "composite")}-${randomBytes(4).toString("hex")}`;
  const outDir = join(lensDir(cwd, lensSlug), "renders", todayFolder());
  const outPath = join(outDir, `${imageSlug}.png`);
  let bytes: Buffer;
  try {
    const dl = await fetch(generatedUrl);
    if (!dl.ok) return errorResult("magnific_task_failed", `download ${dl.status}`);
    bytes = Buffer.from(new Uint8Array(await dl.arrayBuffer()));
    await mkdir(outDir, { recursive: true });
    await writeFile(outPath, bytes);
  } catch (err) {
    log.error({ err }, "image download failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "download failed");
  }

  onProgress("saved", outPath);
  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "saved",
    imageSlug,
    outPath,
    engine: "nano-banana-pro",
  });
  await appendPerLensLog(cwd, lensSlug, {
    ts: new Date().toISOString(),
    clientUserId,
    lens: lensSlug,
    inputs: { variables, uploads: inputs },
    promptTrimmedChars: fused.trimmedChars,
    status: "saved",
    file: outPath,
  });

  const dataUri = `data:image/png;base64,${bytes.toString("base64")}`;
  return { ok: true, imageUrl: dataUri, imageSlug };
}

// ---- Render history --------------------------------------------------------

// How many of the most-recent saved renders to replay to the portal. Each is
// read from disk and base64-encoded as a data-URI, so this bounds the response
// size (~400KB/image at 1k) — a deliberate, documented cap, not silent
// truncation. Older renders stay on disk; they're just not replayed.
const RENDER_HISTORY_LIMIT = 24;

export interface SavedRender {
  imageUrl: string; // data:image/webp;base64,...
  subject: string;
  ts: string;
  imageSlug: string | null;
  variables?: Record<string, string>;
}

interface PerLensLogEntry {
  ts?: string;
  status?: string;
  file?: string;
  inputs?: { subject?: string; variables?: Record<string, string> };
}

/**
 * Replays a lens's saved renders from its `renders/log.jsonl`, newest last
 * (chronological — matches the portal's append convention). Only `status:
 * "saved"` entries with a readable file are returned. Missing log / unreadable
 * files degrade gracefully to fewer results, never a throw.
 */
export async function listSavedRenders(
  cwd: string,
  lensSlug: string,
  logger: pino.Logger,
): Promise<SavedRender[]> {
  const log = logger.child({ component: "lens-renderer", lensSlug });
  const logPath = join(lensDir(cwd, lensSlug), "renders", "log.jsonl");

  let raw: string;
  try {
    raw = await readFile(logPath, "utf8");
  } catch {
    return []; // no history yet
  }

  const saved: PerLensLogEntry[] = [];
  for (const line of raw.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const entry = JSON.parse(trimmed) as PerLensLogEntry;
      if (entry.status === "saved" && typeof entry.file === "string") {
        saved.push(entry);
      }
    } catch {
      // skip malformed line
    }
  }

  // Keep the most-recent N in chronological order.
  const recent = saved.slice(-RENDER_HISTORY_LIMIT);
  if (saved.length > RENDER_HISTORY_LIMIT) {
    log.info(
      { total: saved.length, replayed: recent.length },
      "render history capped to most-recent window",
    );
  }

  const results: SavedRender[] = [];
  for (const entry of recent) {
    try {
      const bytes = await readFile(entry.file as string);
      const dataUri = `data:${mimeForPath(entry.file as string)};base64,${bytes.toString("base64")}`;
      const imageSlug =
        typeof entry.file === "string"
          ? (entry.file
              .split("/")
              .pop()
              ?.replace(/\.(webp|png|jpe?g|mp4|webm)$/i, "") ?? null)
          : null;
      results.push({
        imageUrl: dataUri,
        subject: entry.inputs?.subject ?? "",
        ts: entry.ts ?? "",
        imageSlug,
        ...(entry.inputs?.variables ? { variables: entry.inputs.variables } : {}),
      });
    } catch (err) {
      // File referenced in the log is gone (moved/deleted) — skip it.
      log.warn({ err, file: entry.file }, "saved render file unreadable, skipping");
    }
  }
  return results;
}

// ---- Helpers ---------------------------------------------------------------

function errorResult(errorCode: string, error: string): RunLensRenderResult {
  return { ok: false, error, errorCode };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function appendPerLensLog(
  cwd: string,
  lensSlug: string,
  entry: Record<string, unknown>,
): Promise<void> {
  const dir = join(lensDir(cwd, lensSlug), "renders");
  const file = join(dir, "log.jsonl");
  try {
    await mkdir(dir, { recursive: true });
    await appendFile(file, `${JSON.stringify(entry)}\n`);
  } catch {
    // best-effort log — never throw from here
  }
}

async function appendRenderLog(
  appostleHome: string,
  clientUserId: string,
  entry: Record<string, unknown>,
): Promise<void> {
  const dir = join(appostleHome, "lenses");
  const file = join(dir, `renders-${sha8(clientUserId)}.jsonl`);
  try {
    await mkdir(dir, { recursive: true });
    await appendFile(file, `${JSON.stringify(entry)}\n`);
  } catch {
    // best-effort log — never throw from here
  }
}

function sha8(input: string): string {
  return createHash("sha256").update(input, "utf8").digest("hex").slice(0, 12);
}
